import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuthConfig } from '../components/auth/usePublicSignup';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;
const LOGO_URL = '/ciaolead-logo-full.png';

export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_BYTES = 72;
export const INVALID_LINK_MESSAGE = 'This invitation link is invalid or has expired. If you already '
  + 'activated your account, please sign in.';

// UTF-8 length without TextEncoder (absent in some test environments): 1-4 bytes per code point.
const byteLength = (value) => {
  let bytes = 0;
  for (const ch of value) {
    const code = ch.codePointAt(0);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code < 0x10000) bytes += 3;
    else bytes += 4;
  }
  return bytes;
};

/**
 * PILOT-ACCESS-003A: activate an account from a personal invitation.
 *
 * The token arrives in the URL FRAGMENT (`/accept-invite#<token>`), which browsers never send to
 * a server; it is read once on mount and immediately removed from the address bar and history
 * entry, kept only in memory, and posted in the request body. The person chooses a password
 * (12+ characters, at most 72 bytes: the server refuses rather than truncates) and accepts the
 * privacy policy; on success they are sent to the normal login. No session is created here.
 */
const AcceptInvite = () => {
  const navigate = useNavigate();
  const config = useAuthConfig();
  const tokenRef = useRef(null);
  const [hasToken, setHasToken] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fragment = window.location.hash ? window.location.hash.slice(1) : '';
    tokenRef.current = fragment || null;
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    setHasToken(Boolean(fragment));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (byteLength(password) > MAX_PASSWORD_BYTES) {
      setError(`Password must be at most ${MAX_PASSWORD_BYTES} bytes (shorter, or fewer accented or special characters)`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match');
      return;
    }
    if (!consent) {
      setError('Please accept the Privacy Policy to continue');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/auth/invitations/accept`, {
        token: tokenRef.current, password, privacy_consent: true,
      });
      tokenRef.current = null;
      toast.success('Account activated — please sign in');
      navigate('/login');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Activation failed. Please try again later.');
    } finally {
      setBusy(false);
    }
  };

  const disabled = config?.invitations_enabled === false;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-page)] world-stars p-8"
      data-testid="accept-invite-page">
      <div className="w-full max-w-md space-y-8 world-card p-8 lg:p-10">
        <div className="flex justify-center lg:justify-start">
          <img src={LOGO_URL} alt="CiaoLead" className="max-w-[200px] w-full h-auto object-contain" />
        </div>
        <Card className="border-0 shadow-none bg-transparent">
          <CardHeader className="px-0">
            <CardTitle className="text-3xl font-bold text-white">Activate your account</CardTitle>
            <CardDescription className="text-slate-400 text-base">
              Choose a password to finish joining from your invitation.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0 space-y-5">
            {(hasToken === false || disabled) && (
              <p className="text-sm text-slate-300" data-testid="accept-invite-invalid">
                {disabled ? 'Invitations are not available.' : INVALID_LINK_MESSAGE}
              </p>
            )}
            {hasToken && !disabled && (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="accept-invite-form">
                <div className="space-y-2">
                  <Label htmlFor="accept-password" className="text-slate-200 font-medium">Password</Label>
                  <Input id="accept-password" type="password" autoComplete="new-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} className="h-11 world-input"
                    data-testid="accept-password" />
                  <p className="text-xs text-slate-500">At least {MIN_PASSWORD_LENGTH} characters.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accept-confirm" className="text-slate-200 font-medium">Confirm password</Label>
                  <Input id="accept-confirm" type="password" autoComplete="new-password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} className="h-11 world-input"
                    data-testid="accept-confirm" />
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-300" htmlFor="accept-privacy">
                  <input id="accept-privacy" type="checkbox" checked={consent}
                    onChange={(e) => setConsent(e.target.checked)} className="mt-1"
                    data-testid="accept-privacy" />
                  <span>
                    I have read and accept the{' '}
                    <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</Link>
                  </span>
                </label>
                {error && <p className="text-sm text-red-400" role="alert" data-testid="accept-error">{error}</p>}
                <Button type="submit" disabled={busy} className="w-full h-11 world-btn-primary font-medium"
                  data-testid="accept-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activate account'}
                </Button>
              </form>
            )}
            <p className="text-center text-sm text-slate-400">
              <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium"
                data-testid="accept-login-link">Go to sign in</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvite;
